import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Skeleton } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as SurprisedBuddy } from '../../../assets/icons/v3/surprised-buddy.svg';
import BdsLinearProgressIndicator from '../../../shared/components/v3/BdsLinearProgressIndicator/BdsLinearProgressIndicator';
import BdsButton from '../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import BdsCardContainer from '../../../shared/components/v3/Cards/BdsCardContainer/BdsCardContainer';
import StatusCard from '../../../shared/components/v3/Cards/StatusCard/StatusCard';
import SecondaryHero from '../../../shared/components/v3/Heros/SecondaryHero/SecondaryHero';
import ManageEmailsContainer from '../../../shared/components/v3/ManageEmailsContainer/ManageEmailsContainer';
import BdsModal from '../../../shared/components/v3/Modals/BdsModal/BdsModal';
import Toast from '../../../shared/components/v3/Toast/Toast';
import { COLLABORATORS_HOME_ROUTE, MANAGE_COLLABORATORS_ROUTE } from '../../../shared/constants/auth';
import { ARROW_RIGHT_ICON } from '../../../shared/constants/images';
import {
  CLICK_ACTION,
  REMOVE_COLLABORATORS,
  REMOVE_COLLABORATORS_CANCEL,
  REMOVE_COLLABORATORS_CONFIRM
} from '../../../shared/constants/tagManager';
import {
  COLLABORATOR_LIMIT_ERROR_TOAST_NE,
  DEFAULT_TOAST_DATA,
  GENERIC_ERROR_TOAST
} from '../../../shared/constants/toast';
import { CompanyContext } from '../../../shared/context/company/companyContext';
import { getCollaboratorsTexts } from '../../../shared/handlers/dataHandlers';
import { ICollaboratorsEmailData } from '../../../shared/interfaces/collaborators';
import { IToast } from '../../../shared/interfaces/toast';
import Companies from '../../../shared/services/api/collaborators/collaborators';
import TagManagerService from '../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_COLLABORATORS } from '../../../shared/translations/collaborators';
import { TRANSLATION_MANAGE_COLLABORATORS } from '../../../shared/translations/manageCollaborators';
import { toLocalStorage } from '../../../shared/utils/utils';
import './DeleteSubscriptions.scss';

const DeleteSubscriptions = () => {
  const { t } = useTranslation();
  const [emails, setEmails] = useState<ICollaboratorsEmailData[]>([]);
  const [existingEmails, setExistingEmails] = useState<string[]>([]);
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [toastData, setToastData] = useState<IToast>(DEFAULT_TOAST_DATA);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [disableButton, setDisableButton] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorEmails, setErrorEmails] = useState<string[]>([]);
  const [resetEmails, setResetEmails] = useState<boolean>(false);

  const navigate = useNavigate();

  const { company } = useContext(CompanyContext);

  const { deleteModalTitle, deleteModalDescription } = useMemo(() => getCollaboratorsTexts(emails.length), [emails]);

  const errorAmount = useMemo(() => {
    return emails.filter((email) => errorEmails.includes(email.emailString)).length;
  }, [emails, errorEmails]);

  const isEmailInvalid = useMemo(() => {
    return !emails.length || emails.some((email) => !email.isValidEmail) || !!errorAmount;
  }, [emails, errorAmount]);

  useEffect(() => {
    setDisableButton(isEmailInvalid);
  }, [isEmailInvalid]);

  const handleCloseToast = () => {
    setOpenToast(false);
  };

  const handleChangeEmails = useCallback((updatedEmails: ICollaboratorsEmailData[], updatedErrorEmails: string[]) => {
    setEmails(updatedEmails);
    setErrorEmails(updatedErrorEmails);
  }, []);

  const setMissingEmails = (missingEmails: string[]) => {
    setEmails((prevEmails) =>
      prevEmails.map((email) => ({
        emailString: email.emailString,
        isValidEmail: !missingEmails.includes(email.emailString),
        id: email.id
      }))
    );
  };

  const verifyEmails = useCallback(async () => {
    try {
      setErrorEmails([]);
      setLoading(true);
      const emailsToSend = emails.map((email) => email.emailString);
      const response = await Companies.validateMembers(company.id, emailsToSend);
      TagManagerService.pushEvent(CLICK_ACTION, REMOVE_COLLABORATORS);
      if (response.error) {
        setErrorEmails([]);
        setExistingEmails([]);
        setMissingEmails([]);
        setResetEmails(true);
        setToastData(COLLABORATOR_LIMIT_ERROR_TOAST_NE);
        setOpenToast(true);
        return;
      }
      setExistingEmails(response.existingEmails);
      if (response.missingEmails.length) {
        setErrorEmails(response.missingEmails);
        setMissingEmails(response.missingEmails);
      }
      if (!response.missingEmails.length && response.existingEmails.length) {
        setOpenDeleteModal(true);
      }
    } catch {
      setToastData(GENERIC_ERROR_TOAST);
      setOpenToast(true);
    } finally {
      setLoading(false);
      setResetEmails(false);
    }
  }, [emails, company]);

  const onDeleteCollaborators = useCallback(async () => {
    setLoading(true);
    try {
      await Companies.deleteMembers(company.id, existingEmails);
      const subscriptionDeletedValue = existingEmails.length > 1 ? 'true, true' : 'true, false';
      toLocalStorage('subscriptionDeleted', subscriptionDeletedValue);
      navigate(COLLABORATORS_HOME_ROUTE);
    } catch {
      setOpenToast(true);
    }
  }, [company.id, existingEmails]);

  const handleActionBack = () => {
    navigate(MANAGE_COLLABORATORS_ROUTE, { state: { method: 'deleteSubscriptions' } });
  };

  const renderStatusCard = () => {
    if (errorAmount) {
      return (
        <StatusCard
          type="alert"
          message={
            <Trans
              i18nKey={TRANSLATION_MANAGE_COLLABORATORS.deleteSubscriptionsSection.checkEmails.alert}
              components={{
                b: <b />
              }}
              count={errorAmount}
              values={{
                emails: errorAmount
              }}
            />
          }
        />
      );
    }
    return (
      <StatusCard
        type="success"
        message={t(TRANSLATION_MANAGE_COLLABORATORS.deleteSubscriptionsSection.checkEmails.sucess)}
      />
    );
  };

  return (
    <div className="manage-collaborators-container">
      <SecondaryHero
        title={t(TRANSLATION_MANAGE_COLLABORATORS.hero.deleteSubscriptionsSection.title, { count: 2 })}
        description={t(TRANSLATION_MANAGE_COLLABORATORS.hero.deleteSubscriptionsSection.subtitle, { count: 2 })}
        color="surface-container-quaternary-light"
        bottomContent={<BdsLinearProgressIndicator variant="subtle" size="s" progress={100} />}
        backAction={handleActionBack}
      />
      <div className="manage-collaborators-content">
        <div className="delete-subscriptions-section">
          <h3 className="title">{t(TRANSLATION_MANAGE_COLLABORATORS.deleteSubscriptionsSection.title)}</h3>
          <BdsCardContainer paddingSize="padding-l" borderRadiusSize="radius-l" className="text-area-container">
            <div className="information-counter">
              {loading ? (
                <>
                  <Skeleton className="skeletons" animation={false} height="30px" />
                  <Skeleton className="skeletons" animation={false} height="35px" />
                </>
              ) : (
                <>
                  <p>
                    {t(TRANSLATION_MANAGE_COLLABORATORS.deleteSubscriptionsSection.counterDescription, {
                      count: emails.length
                    })}
                  </p>
                  <h3>
                    {t(TRANSLATION_MANAGE_COLLABORATORS.deleteSubscriptionsSection.numberOfDescription, {
                      user: emails.length,
                      count: emails.length
                    })}
                  </h3>
                </>
              )}
              {!!errorEmails.length && renderStatusCard()}
            </div>

            <ManageEmailsContainer
              errorEmails={errorEmails}
              handleChangeEmails={handleChangeEmails}
              loading={loading}
              hideNonErrorTags={!!errorAmount}
              reset={resetEmails}
            />
          </BdsCardContainer>
        </div>
        <div className="manage-collaborators-button">
          <BdsButton
            variant="primary"
            size="l"
            action={verifyEmails}
            disabled={disableButton}
            loading={loading}
            iconRight={ARROW_RIGHT_ICON}
          >
            {t(TRANSLATION_MANAGE_COLLABORATORS.buttonNext)}
          </BdsButton>
        </div>
        <Toast
          title={t(toastData.title)}
          description={t(toastData.description)}
          icon={toastData.icon}
          open={openToast}
          button={toastData.button}
          buttonAction={handleCloseToast}
          handleClose={handleCloseToast}
        />
        <BdsModal
          open={openDeleteModal}
          title={t(deleteModalTitle, { users: emails.length })}
          description={
            <Trans
              i18nKey={deleteModalDescription}
              values={{ user_email: emails.length === 1 ? emails[0].emailString : '' }}
              components={{ b: <b /> }}
            />
          }
          icon={<SurprisedBuddy />}
          loading={loading}
          primaryAction={{
            label: t(TRANSLATION_COLLABORATORS.table.deleteModal.buttonConfirm),
            onClick: () => {
              TagManagerService.pushEvent(CLICK_ACTION, REMOVE_COLLABORATORS_CONFIRM, {
                number: existingEmails.length
              });
              onDeleteCollaborators();
            }
          }}
          secondaryAction={{
            label: t(TRANSLATION_COLLABORATORS.table.deleteModal.buttonClose),
            onClick: () => {
              TagManagerService.pushEvent(CLICK_ACTION, REMOVE_COLLABORATORS_CANCEL);
              setOpenDeleteModal(false);
            }
          }}
        />
      </div>
    </div>
  );
};

export default DeleteSubscriptions;
